/**
 * fixedAssetHelper.js — Firestore + posting glue for the Fixed Assets module.
 *
 * Category seeding (maps each category to the seeded postable L5 COA accounts),
 * asset-code generation, and acquisition posting through the UNIFIED transaction
 * architecture (createTransaction + generateSubTransactions as a JV), so assets
 * flow into Trial Balance / Balance Sheet / P&L like every other posting.
 */
import { collection, doc, getDoc, getDocs, setDoc, updateDoc, writeBatch, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { findAccountByCode } from './journalEntryHelper';
import { createTransaction, updateTransaction, deleteTransaction } from './transactionHelper';
import { generateSubTransactions } from './subTransactionHelper';
import { SYSTEM_ACCOUNTS } from '../config/tranTypes';
import {
  FBR_DEFAULT_RATES, computeDisposalResult, depreciationFloor,
  appreciationOf, appreciationDepreciationOf, bookValueOf, hasAppreciation, depreciableAppreciationOf,
} from './depreciationHelper';
import { invalidateMaster } from './masterDataStore';

// Default categories mapped to the postable L5 accounts seeded in
// defaultChartOfAccounts.js. Doc id == key for idempotent seeding.
export const DEFAULT_ASSET_CATEGORIES = [
  { key: 'land',             categoryName: 'Land',                 assetCode: '12105001', accumDepCode: null,       defaultMethod: 'none',             defaultRate: FBR_DEFAULT_RATES.land,             defaultUsefulLifeYears: 0 },
  { key: 'building',         categoryName: 'Building',             assetCode: '12104001', accumDepCode: '12204001', defaultMethod: 'reducing_balance', defaultRate: FBR_DEFAULT_RATES.building,         defaultUsefulLifeYears: 30 },
  { key: 'furniture',        categoryName: 'Furniture & Fixtures', assetCode: '12101001', accumDepCode: '12201001', defaultMethod: 'reducing_balance', defaultRate: FBR_DEFAULT_RATES.furniture,        defaultUsefulLifeYears: 10 },
  { key: 'office_equipment', categoryName: 'Office Equipment',     assetCode: '12102001', accumDepCode: '12202001', defaultMethod: 'reducing_balance', defaultRate: FBR_DEFAULT_RATES.office_equipment, defaultUsefulLifeYears: 8 },
  { key: 'vehicles',         categoryName: 'Vehicles',             assetCode: '12103001', accumDepCode: '12203001', defaultMethod: 'reducing_balance', defaultRate: FBR_DEFAULT_RATES.vehicles,         defaultUsefulLifeYears: 8 },
  { key: 'plant_machinery',  categoryName: 'Plant & Machinery',    assetCode: '12106001', accumDepCode: '12205001', defaultMethod: 'reducing_balance', defaultRate: FBR_DEFAULT_RATES.plant_machinery,  defaultUsefulLifeYears: 10 },
];

// Funding options for acquisition posting (Cr side). Only postable L5 accounts
// are offered. "On credit (vendor payable)" is deferred — it needs a specific
// vendor's L5 account, which is a later enhancement.
export const FUNDING_OPTIONS = [
  { value: 'cash',     label: 'Paid by Cash',          accountCode: SYSTEM_ACCOUNTS.CASH_IN_HAND },
  { value: 'bank',     label: 'Paid by Bank',          accountCode: null }, // resolved from selected bank account
  { value: 'capital',  label: "Owner's Capital / Introduced", accountCode: SYSTEM_ACCOUNTS.OPENING_BALANCE_EQUITY },
  { value: 'existing', label: 'Already posted elsewhere (no entry)', accountCode: null },
];

const categoriesRef = (companyId) => collection(db, 'companies', companyId, 'fixedAssetCategories');
const assetsRef = (companyId) => collection(db, 'companies', companyId, 'fixedAssets');

/**
 * Seed/repair the fixed-asset categories. Idempotent — only creates a category
 * doc that doesn't already exist, resolving the COA account IDs by code. Returns
 * the full category list.
 */
export const ensureFixedAssetCategories = async (companyId, userId) => {
  const snap = await getDocs(categoriesRef(companyId));
  const existing = {};
  snap.docs.forEach(d => { existing[d.id] = { id: d.id, ...d.data() }; });

  const depExpenseAcct = await findAccountByCode(companyId, SYSTEM_ACCOUNTS.DEPRECIATION_EXPENSE);

  let created = 0;
  for (const def of DEFAULT_ASSET_CATEGORIES) {
    if (existing[def.key]) continue;
    const assetAcct = await findAccountByCode(companyId, def.assetCode);
    const accumAcct = def.accumDepCode ? await findAccountByCode(companyId, def.accumDepCode) : null;

    const data = {
      key: def.key,
      categoryName: def.categoryName,
      assetAccountId: assetAcct?.id || null,
      assetAccountCode: def.assetCode,
      accumDepAccountId: accumAcct?.id || null,
      accumDepAccountCode: def.accumDepCode || null,
      depExpenseAccountId: depExpenseAcct?.id || null,
      depExpenseAccountCode: SYSTEM_ACCOUNTS.DEPRECIATION_EXPENSE,
      defaultMethod: def.defaultMethod,
      defaultRate: def.defaultRate,
      defaultUsefulLifeYears: def.defaultUsefulLifeYears,
      isActive: true,
      createdAt: new Date().toISOString(),
      createdBy: userId || '',
    };
    await setDoc(doc(categoriesRef(companyId), def.key), data);
    created += 1;
  }

  return { created, categories: await loadFixedAssetCategories(companyId) };
};

export const loadFixedAssetCategories = async (companyId) => {
  const snap = await getDocs(categoriesRef(companyId));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const loadFixedAssets = async (companyId) => {
  const snap = await getDocs(query(assetsRef(companyId), orderBy('assetCode', 'asc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Next asset code FA-0001, FA-0002, … scanning existing assets.
 */
export const generateAssetCode = async (companyId) => {
  const snap = await getDocs(assetsRef(companyId));
  let max = 0;
  snap.docs.forEach(d => {
    const code = d.data().assetCode || '';
    const m = /^FA-(\d+)$/.exec(code);
    if (m) max = Math.max(max, parseInt(m[1], 10));
  });
  return `FA-${String(max + 1).padStart(4, '0')}`;
};

/**
 * Post a Fixed-Asset journal (acquisition / disposal) through the unified model.
 * Mirrors the General Journal save: createTransaction (JV) + generateSubTransactions.
 *
 * @param {string} companyId
 * @param {object} params - { docDate, narration, financialYearId, branchId, branchName,
 *                            lines: [{accountId, accountCode, accountName, debitAmount, creditAmount, description}],
 *                            userId }
 * @returns {Promise<{transactionId:string, docNumber:string}>}
 */
export const postFixedAssetJournal = async (companyId, params) => {
  const { docDate, narration, financialYearId, branchId = null, branchName = null, lines, userId, tranTypeCode = 'JV', extraFields = {} } = params;

  const cleanLines = (lines || [])
    .filter(l => l.accountId && ((Number(l.debitAmount) || 0) > 0 || (Number(l.creditAmount) || 0) > 0))
    .map((l, i) => ({
      accountId: l.accountId,
      accountCode: l.accountCode || '',
      accountName: l.accountName || '',
      debitAmount: Number(l.debitAmount) || 0,
      creditAmount: Number(l.creditAmount) || 0,
      description: l.description || narration || '',
      lineNumber: i + 1,
    }));

  const totalDebit = Math.round(cleanLines.reduce((s, l) => s + l.debitAmount, 0) * 100) / 100;
  const totalCredit = Math.round(cleanLines.reduce((s, l) => s + l.creditAmount, 0) * 100) / 100;
  if (cleanLines.length < 2) throw new Error('Need at least two account lines to post.');
  if (Math.abs(totalDebit - totalCredit) > 0.01) {
    throw new Error(`Out of balance: Dr ${totalDebit} ≠ Cr ${totalCredit}`);
  }

  const data = {
    tranTypeCode,
    docDate,
    voucherType: 'Journal',
    branchId,
    branchName,
    reference: '',
    narration,
    notes: '',
    status: 'Posted',
    financialYearId,
    totalDebit,
    totalCredit,
    lines: cleanLines,
    ...extraFields,
  };

  const created = await createTransaction(companyId, data, userId);

  await generateSubTransactions(companyId, {
    transactionId: created.id,
    tranTypeCode,
    docNumber: created.docNumber,
    docDate,
    financialYearId,
    branchId,
    branchName,
    status: 'Posted',
    manualLines: cleanLines.map(l => ({
      accountId: l.accountId,
      accountCode: l.accountCode,
      accountName: l.accountName,
      debitAmount: l.debitAmount,
      creditAmount: l.creditAmount,
      description: l.description,
    })),
  }, userId);

  return { transactionId: created.id, docNumber: created.docNumber };
};

/**
 * Post an asset acquisition: Dr category asset account / Cr funding account.
 * Returns null when fundingMode === 'existing' (caller links manually, no entry).
 */
export const postAcquisition = async (companyId, { asset, category, fundingAccount, financialYearId, branchId, branchName, userId }) => {
  const cost = Number(asset.acquisitionCost) || 0;
  if (cost <= 0) throw new Error('Acquisition cost must be greater than zero.');
  if (!category?.assetAccountId) throw new Error('Category has no linked asset account. Run "Seed / Repair Categories".');
  if (!fundingAccount?.id) throw new Error('Could not resolve the funding account.');

  return postFixedAssetJournal(companyId, {
    docDate: asset.acquisitionDate,
    narration: `Asset acquisition: ${asset.assetName} (${asset.assetCode})`,
    financialYearId,
    branchId,
    branchName,
    userId,
    // SEC-01B: marks this voucher as the asset's own acquisition entry, so the
    // Asset Register right (not the Journal Voucher right) covers it.
    extraFields: asset.id ? { assetAcquisitionId: asset.id } : {},
    lines: [
      { accountId: category.assetAccountId, accountCode: category.assetAccountCode, accountName: category.categoryName, debitAmount: cost, creditAmount: 0, description: `Acquired ${asset.assetName}` },
      { accountId: fundingAccount.id, accountCode: fundingAccount.code, accountName: fundingAccount.name, debitAmount: 0, creditAmount: cost, description: `Payment for ${asset.assetName}` },
    ],
  });
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** Resolve an account doc by its Firestore id → { id, code, name }. */
export const resolveAccountById = async (companyId, accountId) => {
  if (!accountId) return null;
  const snap = await getDoc(doc(db, 'companies', companyId, 'accounts', accountId));
  if (!snap.exists()) return null;
  const data = snap.data();
  return { id: snap.id, code: data.code, name: data.name };
};

/**
 * Find a non-cancelled Depreciation Run already posted for a period key (YYYY-MM
 * or YYYY). Single-field query + JS filter to avoid composite indexes.
 */
export const findExistingDepreciationRun = async (companyId, periodKey) => {
  const q = query(collection(db, 'companies', companyId, 'transactions'), where('tranTypeCode', '==', 'DEP'));
  const snap = await getDocs(q);
  const match = snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .find(t => t.periodKey === periodKey && t.status !== 'Cancelled');
  return match || null;
};

/**
 * Post a Depreciation Run (DEP). Dr Depreciation Expense (grand total) /
 * Cr Accumulated Depreciation per category.
 *
 * @param {object} params - {
 *   periodKey, periodLabel, docDate, financialYearId, branchId, branchName, userId,
 *   depExpenseAccount: {id, code, name},
 *   categoryLines: [{ accumDepAccountId, accumDepAccountCode, categoryName, amount }],
 *   runSnapshot: [...]  // per-asset breakdown stored on the txn for audit
 * }
 */
export const postDepreciationRun = async (companyId, params) => {
  const { periodKey, periodLabel, docDate, financialYearId, branchId = null, branchName = null, userId,
    depExpenseAccount, categoryLines, runSnapshot = [] } = params;

  const cats = (categoryLines || []).filter(c => round2(c.amount) > 0);
  const grandTotal = round2(cats.reduce((s, c) => s + round2(c.amount), 0));
  if (grandTotal <= 0) throw new Error('Nothing to depreciate for this period.');
  if (!depExpenseAccount?.id) throw new Error('Depreciation Expense account not found. Run Repair COA.');
  for (const c of cats) {
    if (!c.accumDepAccountId) throw new Error(`Category "${c.categoryName}" has no Accumulated Depreciation account.`);
  }

  const lines = [
    { accountId: depExpenseAccount.id, accountCode: depExpenseAccount.code, accountName: depExpenseAccount.name, debitAmount: grandTotal, creditAmount: 0, description: `Depreciation — ${periodLabel}` },
    ...cats.map(c => ({
      accountId: c.accumDepAccountId,
      accountCode: c.accumDepAccountCode || '',
      accountName: `Accum Dep - ${c.categoryName}`,
      debitAmount: 0,
      creditAmount: round2(c.amount),
      description: `Depreciation ${periodLabel} — ${c.categoryName}`,
    })),
  ];

  return postFixedAssetJournal(companyId, {
    tranTypeCode: 'DEP',
    docDate,
    narration: `Depreciation Run — ${periodLabel}`,
    financialYearId,
    branchId,
    branchName,
    userId,
    lines,
    extraFields: { periodKey, periodLabel, depreciationSnapshot: runSnapshot },
  });
};

/**
 * Apply a posted depreciation run to each asset's running balances.
 * runLines come from buildDepreciationRun().lines (have assetId, amount,
 * newAccumulated, newNBV, fullyDepreciated).
 */
export const applyDepreciationToAssets = async (companyId, runLines, lastDepreciationDate) => {
  const batch = writeBatch(db);
  let count = 0;
  for (const line of runLines) {
    if (!line.assetId || !(line.amount > 0)) continue;
    batch.update(doc(db, 'companies', companyId, 'fixedAssets', line.assetId), {
      accumulatedDepreciation: line.newAccumulated,
      netBookValue: line.newNBV,
      lastDepreciationDate,
      status: line.fullyDepreciated ? 'FullyDepreciated' : 'Active',
      updatedAt: new Date().toISOString(),
      // Asset Appreciation: the part of this charge that fell on the appreciation.
      ...(line.newAppreciationDepreciation != null ? { appreciationDepreciation: round2(line.newAppreciationDepreciation) } : {}),
    });
    count += 1;
    // Firestore batches cap at 500 ops.
    if (count % 450 === 0) { await batch.commit(); }
  }
  if (count % 450 !== 0) await batch.commit();
  return count;
};

/**
 * Post a Fixed Asset Disposal (FAD) and mark the asset Disposed.
 *   Dr Cash/Bank (proceeds) + Dr Accumulated Depreciation
 *   Cr Asset Cost ; Cr Gain  — or —  Dr Loss
 *
 * @param {object} params - { asset, proceeds, proceedsAccount:{id,code,name}|null,
 *   disposalType, disposalDate, financialYearId, branchId, branchName, userId }
 */
export const postDisposal = async (companyId, params) => {
  const { asset, proceeds = 0, proceedsAccount, disposalType, disposalDate, financialYearId, branchId = null, branchName = null, userId } = params;

  const { lines, nbv, gainLoss, isGain, proceedsAmt } = await buildDisposalLines(companyId, { asset, proceeds, proceedsAccount });

  const posted = await postFixedAssetJournal(companyId, {
    tranTypeCode: 'FAD',
    docDate: disposalDate,
    narration: `Disposal (${disposalType}) — ${asset.assetName} (${asset.assetCode})`,
    financialYearId,
    branchId,
    branchName,
    userId,
    lines,
    extraFields: { sourceAssetId: asset.id, disposalType },
  });

  await updateDoc(doc(db, 'companies', companyId, 'fixedAssets', asset.id), {
    status: 'Disposed',
    disposalDate,
    disposalType,
    disposalProceeds: proceedsAmt,
    gainLossAmount: gainLoss,
    disposalTransactionId: posted.transactionId,
    disposalDocNumber: posted.docNumber,
    disposalProceedsAccountId: proceedsAccount?.id || null,
    netBookValue: 0,
    updatedAt: new Date().toISOString(),
    updatedBy: userId || '',
  });

  return { ...posted, nbv, gainLoss, isGain };
};


// ─────────────────────────────────────────────────────────────────────────────
// ACC-23: edit / reverse for acquisitions, depreciation runs and disposals.
// Every change re-writes the SAME voucher (same number) through updateTransaction,
// which replaces its ledger lines in one save, and keeps the asset register in step.
// ─────────────────────────────────────────────────────────────────────────────

const cleanJournalLines = (lines, narration) => (lines || [])
  .filter(l => l.accountId && ((Number(l.debitAmount) || 0) > 0 || (Number(l.creditAmount) || 0) > 0))
  .map((l, i) => ({
    accountId: l.accountId,
    accountCode: l.accountCode || '',
    accountName: l.accountName || '',
    debitAmount: round2(l.debitAmount),
    creditAmount: round2(l.creditAmount),
    description: l.description || narration || '',
    lineNumber: i + 1,
  }));

/** Re-write an existing fixed-asset voucher (JV / DEP / FAD) in place. */
export const updateFixedAssetJournal = async (companyId, transactionId, { docDate, narration, lines, userId, extraFields = {} }) => {
  const clean = cleanJournalLines(lines, narration);
  const totalDebit = round2(clean.reduce((s, l) => s + l.debitAmount, 0));
  const totalCredit = round2(clean.reduce((s, l) => s + l.creditAmount, 0));
  if (clean.length < 2) throw new Error('Need at least two account lines to post.');
  if (Math.abs(totalDebit - totalCredit) > 0.01) throw new Error(`Out of balance: Dr ${totalDebit} ≠ Cr ${totalCredit}`);
  await updateTransaction(companyId, transactionId, {
    docDate,
    narration,
    status: 'Posted',
    totalDebit,
    totalCredit,
    lines: clean,
    subTransactionLines: clean.map(({ lineNumber, ...rest }) => rest),
    ...extraFields,
  }, userId);
};

const statusForNbv = (asset, nbv) => {
  if (asset.status === 'Disposed') return 'Disposed';
  const floor = depreciationFloor(asset);
  return nbv <= floor + 0.01 && asset.depreciationMethod !== 'none' && (Number(asset.accumulatedDepreciation) || 0) > 0 ? 'FullyDepreciated' : 'Active';
};

/**
 * Apply per-asset depreciation changes: deltas = { assetId: +/-amount }.
 * partDeltas = { assetId: +/-amount } moves the share charged on appreciation
 * (only assets that have appreciation ever get one).
 */
const adjustAssetsDepreciation = async (companyId, deltas, assetsById, userId, partDeltas = {}) => {
  const ids = Object.keys(deltas).filter(id => round2(deltas[id]) !== 0 && assetsById[id]);
  for (let i = 0; i < ids.length; i += 400) {
    const batch = writeBatch(db);
    ids.slice(i, i + 400).forEach(id => {
      const a = assetsById[id];
      const acc = Math.max(0, round2((Number(a.accumulatedDepreciation) || 0) + deltas[id]));
      const nbv = round2((Number(a.acquisitionCost) || 0) + appreciationOf(a) - acc);
      const pd = round2(partDeltas[id]);
      const apprDep = pd !== 0 ? Math.max(0, round2((Number(a.appreciationDepreciation) || 0) + pd)) : null;
      batch.update(doc(db, 'companies', companyId, 'fixedAssets', id), {
        accumulatedDepreciation: acc,
        netBookValue: nbv,
        status: statusForNbv({ ...a, accumulatedDepreciation: acc }, nbv),
        updatedAt: new Date().toISOString(),
        updatedBy: userId || '',
        ...(apprDep != null ? { appreciationDepreciation: apprDep } : {}),
      });
    });
    // eslint-disable-next-line no-await-in-loop
    await batch.commit();
  }
};

/** All non-cancelled depreciation runs, newest first. */
export const loadDepreciationRuns = async (companyId) => {
  const snap = await getDocs(query(collection(db, 'companies', companyId, 'transactions'), where('tranTypeCode', '==', 'DEP')));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(t => t.status !== 'Cancelled')
    .sort((a, b) => (b.docDate || '').localeCompare(a.docDate || '') || (b.docNumber || '').localeCompare(a.docNumber || ''));
};

const assertNotDisposed = (assetIds, assetsById) => {
  const disposed = assetIds.map(id => assetsById[id]).filter(a => a && a.status === 'Disposed');
  if (disposed.length) {
    throw new Error(`These assets were disposed after this run: ${disposed.map(a => a.assetCode).join(', ')}. Reverse their disposal first.`);
  }
};

/** Delete a depreciation run: removes its voucher + ledger and gives the depreciation back to each asset. */
export const deleteDepreciationRun = async (companyId, run, userId) => {
  const assets = await loadFixedAssets(companyId);
  const byId = {}; assets.forEach(a => { byId[a.id] = a; });
  const snap = (run.depreciationSnapshot || []).filter(r => r.assetId && round2(r.amount) > 0);
  assertNotDisposed(snap.map(r => r.assetId), byId);
  await deleteTransaction(companyId, run.id);
  const deltas = {};
  const partDeltas = {};
  snap.forEach(r => {
    deltas[r.assetId] = round2((deltas[r.assetId] || 0) - round2(r.amount));
    if (round2(r.appreciationPart) > 0) partDeltas[r.assetId] = round2((partDeltas[r.assetId] || 0) - round2(r.appreciationPart));
  });
  await adjustAssetsDepreciation(companyId, deltas, byId, userId, partDeltas);
};

/**
 * Edit a depreciation run's per-asset amounts (and date). rows = [{ assetId, amount }].
 * Same voucher number; ledger + asset balances move by the difference only.
 */
export const updateDepreciationRun = async (companyId, run, { rows, docDate, categories, userId }) => {
  const assets = await loadFixedAssets(companyId);
  const byId = {}; assets.forEach(a => { byId[a.id] = a; });
  const oldAmt = {};
  const oldPart = {};
  (run.depreciationSnapshot || []).forEach(r => {
    if (!r.assetId) return;
    oldAmt[r.assetId] = round2((oldAmt[r.assetId] || 0) + round2(r.amount));
    if (round2(r.appreciationPart) > 0) oldPart[r.assetId] = round2((oldPart[r.assetId] || 0) + round2(r.appreciationPart));
  });
  const newAmt = {};
  (rows || []).forEach(r => { if (r.assetId) newAmt[r.assetId] = round2(r.amount); });
  // The share charged on appreciation moves in proportion to the edited amount
  // (never above the appreciation still left to depreciate).
  const newPart = {};
  const partDeltas = {};
  Object.keys(oldPart).forEach(id => {
    const n = round2(newAmt[id] || 0);
    const a = byId[id];
    const room = a ? Math.max(0, round2(depreciableAppreciationOf(a) - ((Number(a.appreciationDepreciation) || 0) - oldPart[id]))) : oldPart[id];
    newPart[id] = oldAmt[id] > 0 ? Math.max(0, Math.min(n, room, round2(oldPart[id] * (n / oldAmt[id])))) : 0;
    const d = round2(newPart[id] - oldPart[id]);
    if (d !== 0) partDeltas[id] = d;
  });

  const deltas = {};
  new Set([...Object.keys(oldAmt), ...Object.keys(newAmt)]).forEach(id => {
    const d = round2((newAmt[id] || 0) - (oldAmt[id] || 0));
    if (d !== 0) deltas[id] = d;
  });
  assertNotDisposed(Object.keys(deltas), byId);

  // Rebuild the voucher lines from the new amounts.
  const byCat = {};
  Object.entries(newAmt).forEach(([id, amt]) => {
    if (!(amt > 0)) return;
    const a = byId[id];
    if (!a) throw new Error('An asset in this run no longer exists.');
    const cat = (categories || []).find(c => c.id === a.categoryId);
    const k = a.categoryId || 'uncat';
    if (!byCat[k]) byCat[k] = { categoryName: a.categoryName || cat?.categoryName || '', amount: 0, accumDepAccountId: cat?.accumDepAccountId || a.accumDepAccountId || null, accumDepAccountCode: cat?.accumDepAccountCode || '' };
    byCat[k].amount = round2(byCat[k].amount + amt);
  });
  const cats = Object.values(byCat);
  const grandTotal = round2(cats.reduce((s, c) => s + c.amount, 0));
  if (grandTotal <= 0) throw new Error('All amounts are zero — delete the run instead.');
  cats.forEach(c => { if (!c.accumDepAccountId) throw new Error(`Category "${c.categoryName}" has no Accumulated Depreciation account.`); });

  const oldDebit = (run.lines || []).find(l => (Number(l.debitAmount) || 0) > 0);
  const depExpense = oldDebit?.accountId
    ? { id: oldDebit.accountId, code: oldDebit.accountCode, name: oldDebit.accountName }
    : await findAccountByCode(companyId, SYSTEM_ACCOUNTS.DEPRECIATION_EXPENSE);
  if (!depExpense?.id) throw new Error('Depreciation Expense account not found. Run Repair COA.');

  const label = run.periodLabel || run.periodKey || '';
  const lines = [
    { accountId: depExpense.id, accountCode: depExpense.code, accountName: depExpense.name, debitAmount: grandTotal, creditAmount: 0, description: `Depreciation — ${label}` },
    ...cats.map(c => ({ accountId: c.accumDepAccountId, accountCode: c.accumDepAccountCode, accountName: `Accum Dep - ${c.categoryName}`, debitAmount: 0, creditAmount: c.amount, description: `Depreciation ${label} — ${c.categoryName}` })),
  ];
  const snapshot = Object.entries(newAmt).filter(([, amt]) => amt > 0).map(([id, amt]) => {
    const a = byId[id];
    const acc = round2((Number(a.accumulatedDepreciation) || 0) + (deltas[id] || 0));
    return {
      assetId: id, assetCode: a.assetCode, assetName: a.assetName, amount: amt,
      newNBV: round2((Number(a.acquisitionCost) || 0) + appreciationOf(a) - acc),
      ...(newPart[id] > 0 ? { appreciationPart: newPart[id] } : {}),
    };
  });

  await updateFixedAssetJournal(companyId, run.id, {
    docDate: docDate || run.docDate,
    narration: run.narration || `Depreciation Run — ${label}`,
    lines,
    userId,
    extraFields: { depreciationSnapshot: snapshot },
  });
  await adjustAssetsDepreciation(companyId, deltas, byId, userId, partDeltas);
};

/** Voucher lines for a disposal (shared by post + edit). */
const buildDisposalLines = async (companyId, { asset, proceeds, proceedsAccount }) => {
  const cost = round2(asset.acquisitionCost);
  const appr = appreciationOf(asset);
  const accumDep = round2(asset.accumulatedDepreciation);
  const { nbv, gainLoss, isGain } = computeDisposalResult(asset, proceeds);
  const proceedsAmt = round2(proceeds);
  if (proceedsAmt > 0 && !proceedsAccount?.id) throw new Error('Select an account for the disposal proceeds.');
  const [assetAcct, accumAcct, gainAcct, lossAcct] = await Promise.all([
    resolveAccountById(companyId, asset.assetAccountId),
    resolveAccountById(companyId, asset.accumDepAccountId),
    findAccountByCode(companyId, SYSTEM_ACCOUNTS.GAIN_ON_DISPOSAL),
    findAccountByCode(companyId, SYSTEM_ACCOUNTS.LOSS_ON_DISPOSAL),
  ]);
  if (!assetAcct?.id) throw new Error('Asset cost account not found for this asset.');
  const lines = [];
  if (proceedsAmt > 0) lines.push({ accountId: proceedsAccount.id, accountCode: proceedsAccount.code, accountName: proceedsAccount.name, debitAmount: proceedsAmt, creditAmount: 0, description: `Proceeds — ${asset.assetName}` });
  if (accumDep > 0) {
    if (!accumAcct?.id) throw new Error('Accumulated Depreciation account not found for this asset.');
    lines.push({ accountId: accumAcct.id, accountCode: accumAcct.code, accountName: accumAcct.name, debitAmount: accumDep, creditAmount: 0, description: `Reverse accum. dep — ${asset.assetName}` });
  }
  lines.push({ accountId: assetAcct.id, accountCode: assetAcct.code, accountName: assetAcct.name, debitAmount: 0, creditAmount: round2(cost + appr), description: appr > 0 ? `Remove cost + appreciation — ${asset.assetName}` : `Remove cost — ${asset.assetName}` });
  if (gainLoss > 0) {
    if (!gainAcct?.id) throw new Error('Gain on Asset Disposal account not found. Run Repair COA.');
    lines.push({ accountId: gainAcct.id, accountCode: gainAcct.code, accountName: gainAcct.name, debitAmount: 0, creditAmount: round2(gainLoss), description: `Gain on disposal — ${asset.assetName}` });
  } else if (gainLoss < 0) {
    if (!lossAcct?.id) throw new Error('Loss on Asset Disposal account not found. Run Repair COA.');
    lines.push({ accountId: lossAcct.id, accountCode: lossAcct.code, accountName: lossAcct.name, debitAmount: round2(-gainLoss), creditAmount: 0, description: `Loss on disposal — ${asset.assetName}` });
  }
  // Asset Appreciation: the asset's revaluation surplus moves to Retained Earnings.
  if (appr > 0) {
    const [surplusAcct, reAcct] = await Promise.all([
      findRevaluationSurplusAccount(companyId),
      findAccountByCode(companyId, SYSTEM_ACCOUNTS.RETAINED_EARNINGS),
    ]);
    if (!surplusAcct?.id) throw new Error('Revaluation Surplus account not found.');
    if (!reAcct?.id) throw new Error('Retained Earnings account not found. Run Repair COA.');
    lines.push({ accountId: surplusAcct.id, accountCode: surplusAcct.code, accountName: surplusAcct.name, debitAmount: appr, creditAmount: 0, description: `Revaluation surplus released — ${asset.assetName}` });
    lines.push({ accountId: reAcct.id, accountCode: reAcct.code, accountName: reAcct.name, debitAmount: 0, creditAmount: appr, description: `Revaluation surplus to retained earnings — ${asset.assetName}` });
  }
  return { lines, nbv, gainLoss, isGain, proceedsAmt };
};

/** Edit a posted disposal in place (same voucher number). */
export const updateDisposal = async (companyId, { asset, proceeds = 0, proceedsAccount, disposalType, disposalDate, userId }) => {
  if (!asset.disposalTransactionId) throw new Error('This disposal has no voucher to edit — reverse it and dispose again.');
  const built = await buildDisposalLines(companyId, { asset, proceeds, proceedsAccount });
  await updateFixedAssetJournal(companyId, asset.disposalTransactionId, {
    docDate: disposalDate,
    narration: `Disposal (${disposalType}) — ${asset.assetName} (${asset.assetCode})`,
    lines: built.lines,
    userId,
    extraFields: { disposalType },
  });
  await updateDoc(doc(db, 'companies', companyId, 'fixedAssets', asset.id), {
    disposalDate,
    disposalType,
    disposalProceeds: built.proceedsAmt,
    disposalProceedsAccountId: proceedsAccount?.id || null,
    gainLossAmount: built.gainLoss,
    updatedAt: new Date().toISOString(),
    updatedBy: userId || '',
  });
  return built;
};

/** Undo a disposal: deletes its voucher + ledger and puts the asset back in service. */
export const reverseDisposal = async (companyId, asset, userId) => {
  if (asset.disposalTransactionId) await deleteTransaction(companyId, asset.disposalTransactionId);
  const acc = round2(asset.accumulatedDepreciation);
  const nbv = round2((Number(asset.acquisitionCost) || 0) + appreciationOf(asset) - acc);
  await updateDoc(doc(db, 'companies', companyId, 'fixedAssets', asset.id), {
    status: statusForNbv({ ...asset, status: 'Active' }, nbv),
    netBookValue: nbv,
    disposalDate: null,
    disposalType: null,
    disposalProceeds: null,
    disposalProceedsAccountId: null,
    gainLossAmount: null,
    disposalTransactionId: null,
    disposalDocNumber: null,
    updatedAt: new Date().toISOString(),
    updatedBy: userId || '',
  });
};

/**
 * Save an edited asset with ALL fields. Keeps the acquisition voucher in step:
 *  - had a voucher + still funded      → voucher re-written (amount/date/accounts)
 *  - had a voucher + now "existing"    → voucher deleted
 *  - no voucher + now funded           → new acquisition voucher posted
 */
export const updateAssetWithAcquisition = async (companyId, { oldAsset, fields, category, fundingMode, fundingAccount, financialYearId, userId }) => {
  const cost = round2(fields.acquisitionCost);
  const costChanged = cost !== round2(oldAsset.acquisitionCost);
  const catChanged = (fields.categoryId || '') !== (oldAsset.categoryId || '');
  if (oldAsset.status === 'Disposed' && (costChanged || catChanged)) {
    throw new Error('This asset is disposed. Reverse the disposal before changing its cost or category.');
  }
  if (catChanged && hasAppreciation(oldAsset)) {
    throw new Error('This asset has appreciation entries. Delete them (Asset Appreciation) before changing its category.');
  }
  const acc = round2(oldAsset.accumulatedDepreciation);
  // Only the depreciation charged on cost counts here (the rest was charged on appreciation).
  if (cost < round2(acc - appreciationDepreciationOf(oldAsset))) throw new Error('Cost cannot be less than the depreciation already charged.');

  const update = {
    ...fields,
    acquisitionCost: cost,
    categoryName: category?.categoryName || oldAsset.categoryName || '',
    assetAccountId: category?.assetAccountId || oldAsset.assetAccountId || null,
    accumDepAccountId: category?.accumDepAccountId || oldAsset.accumDepAccountId || null,
    fundingMode,
    fundingAccountId: fundingAccount?.id || null,
    updatedAt: new Date().toISOString(),
    updatedBy: userId || '',
  };
  const apprDepNow = Number(oldAsset.appreciationDepreciation) || 0;
  if (apprDepNow > 0 && apprDepNow > depreciableAppreciationOf({ ...oldAsset, ...update }) + 0.005) {
    throw new Error('Depreciation has already been charged on this asset\'s appreciation, and with this change part of it could no longer be depreciated. Delete or edit the later depreciation runs first.');
  }
  if (oldAsset.status !== 'Disposed') {
    const nbv = round2(cost + appreciationOf(oldAsset) - acc);
    update.netBookValue = nbv;
    update.status = statusForNbv({ ...oldAsset, ...update, accumulatedDepreciation: acc }, nbv);
  }

  const narration = `Asset acquisition: ${fields.assetName} (${oldAsset.assetCode})`;
  if (oldAsset.sourceTransactionId) {
    if (!fundingAccount) {
      await deleteTransaction(companyId, oldAsset.sourceTransactionId);
      update.sourceTransactionId = null;
      update.sourceDocNumber = null;
    } else {
      if (!category?.assetAccountId) throw new Error('Category has no linked asset account. Run "Seed / Repair Categories".');
      await updateFixedAssetJournal(companyId, oldAsset.sourceTransactionId, {
        docDate: fields.acquisitionDate,
        narration,
        userId,
        lines: [
          { accountId: category.assetAccountId, accountCode: category.assetAccountCode, accountName: category.categoryName, debitAmount: cost, creditAmount: 0, description: `Acquired ${fields.assetName}` },
          { accountId: fundingAccount.id, accountCode: fundingAccount.code, accountName: fundingAccount.name, debitAmount: 0, creditAmount: cost, description: `Payment for ${fields.assetName}` },
        ],
      });
    }
  } else if (fundingAccount) {
    const posted = await postAcquisition(companyId, {
      asset: { ...oldAsset, ...update },
      category,
      fundingAccount,
      financialYearId: financialYearId || oldAsset.financialYearId,
      branchId: oldAsset.branchId || null,
      branchName: oldAsset.branchName || null,
      userId,
    });
    update.sourceTransactionId = posted.transactionId;
    update.sourceDocNumber = posted.docNumber;
  }

  await updateDoc(doc(db, 'companies', companyId, 'fixedAssets', oldAsset.id), update);
};

// ─────────────────────────────────────────────────────────────────────────────
// ASSET APPRECIATION (upward revaluation) — module 'fixed_assets_appreciation'.
//   Voucher APR: Dr each asset's Fixed Asset account / Cr Revaluation Surplus.
//   The asset keeps its original cost; the increase is kept separately in
//   appreciationAmount + appreciationEntries [{ transactionId, docNumber, date, amount }].
// ─────────────────────────────────────────────────────────────────────────────

// Equity reserve made on first use (it is not part of the default chart, so
// companies that never use appreciation see no change in their accounts).
// The accounts are found by their marker (systemKey), never by code alone: a
// company may already have its own equity group at 33 / 331 / … (the next
// free codes under Equity), and that account must never be used for this.
export const REVALUATION_SURPLUS_KEY = 'revaluationSurplus';
const REVALUATION_GROUP_KEY = 'revaluationReserves';

/** The Revaluation Surplus account made by this module, or null. */
export const findRevaluationSurplusAccount = async (companyId) => {
  const snap = await getDocs(query(collection(db, 'companies', companyId, 'accounts'), where('systemKey', '==', REVALUATION_SURPLUS_KEY), limit(1)));
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, code: d.data().code, name: d.data().name };
};

/**
 * Find the Revaluation Surplus account, creating it the first time:
 *   Equity (3) → Reserves (33) → Capital Reserves (331) → Revaluation Surplus (33101)
 *   → Surplus on Revaluation of Fixed Assets (33101001)
 * If 33 (or a code below it) is already used, the next free group (34, 35 …) is used.
 */
export const ensureRevaluationSurplusAccount = async (companyId, userId) => {
  const found = await findRevaluationSurplusAccount(companyId);
  if (found) return found;
  const root = await findAccountByCode(companyId, '3');
  if (!root?.id) throw new Error('Equity group (3) not found in your Chart of Accounts. Run Repair COA.');

  const accountsRef = collection(db, 'companies', companyId, 'accounts');
  const eqSnap = await getDocs(query(accountsRef, where('code', '>=', '3'), where('code', '<', '4')));
  const used = new Set(eqSnap.docs.map(d => String(d.data().code ?? '')));
  let g = null;
  for (let n = 3; n <= 9 && !g; n += 1) {
    const c2 = `3${n}`;
    const codes = [c2, `${c2}1`, `${c2}101`, `${c2}101001`];
    if (codes.every(c => !used.has(c))) g = codes;
  }
  if (!g) throw new Error('No free account code under Equity for the Revaluation Surplus account. Please contact support.');

  const chain = [
    { code: g[0], name: 'Reserves', level: 2, isPostable: false, description: 'Capital and revenue reserves', systemKey: REVALUATION_GROUP_KEY },
    { code: g[1], name: 'Capital Reserves', level: 3, isPostable: false, description: 'Reserves not available for distribution', systemKey: REVALUATION_GROUP_KEY },
    { code: g[2], name: 'Revaluation Surplus', level: 4, isPostable: false, description: 'Surplus on revaluation of fixed assets', allowUserAccounts: true, systemKey: REVALUATION_GROUP_KEY },
    { code: g[3], name: 'Surplus on Revaluation of Fixed Assets', level: 5, isPostable: true, description: 'Asset Appreciation (upward revaluation) of fixed assets', allowUserAccounts: false, systemKey: REVALUATION_SURPLUS_KEY },
  ];
  const batch = writeBatch(db);
  const now = new Date().toISOString();
  let parentId = root.id;
  let path = [...(Array.isArray(root.path) ? root.path : []), root.id];
  let result = null;
  chain.forEach(seed => {
    const ref = doc(accountsRef);
    batch.set(ref, {
      code: seed.code,
      name: seed.name,
      level: seed.level,
      accountType: 'equity',
      balanceType: 'credit',
      parentId,
      path,
      isSystemAccount: true,
      isPostable: seed.isPostable,
      isActive: true,
      description: seed.description,
      linkedEntityType: null,
      allowUserAccounts: seed.allowUserAccounts !== false,
      systemKey: seed.systemKey,
      openingBalance: 0,
      currentBalance: 0,
      createdAt: now,
      updatedAt: now,
      createdBy: userId || '',
    });
    if (seed.level === 5) result = { id: ref.id, code: seed.code, name: seed.name };
    path = [...path, ref.id];
    parentId = ref.id;
  });
  try {
    await batch.commit();
  } catch (e) {
    if (/permission/i.test(String(e?.code || e?.message || ''))) {
      throw new Error('The Revaluation Surplus account needs to be added to your Chart of Accounts first. Ask your admin to post the first appreciation (or give you Chart of Accounts edit rights).');
    }
    throw e;
  }
  invalidateMaster(companyId, 'accounts');
  return result;
};

/** All non-cancelled appreciation vouchers, newest first. */
export const loadAppreciations = async (companyId) => {
  const snap = await getDocs(query(collection(db, 'companies', companyId, 'transactions'), where('tranTypeCode', '==', 'APR')));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(t => t.status !== 'Cancelled')
    .sort((a, b) => (b.docDate || '').localeCompare(a.docDate || '') || (b.docNumber || '').localeCompare(a.docNumber || ''));
};

const sumEntries = (entries) => round2((entries || []).reduce((s, e) => s + round2(e.amount), 0));

/** Voucher lines: Dr each Fixed Asset account (grouped) / Cr Revaluation Surplus. */
const buildAppreciationLines = async (companyId, items, surplusAcct, docDate) => {
  const byAcct = {};
  items.forEach(({ asset, amount }) => {
    const k = asset.assetAccountId;
    if (!k) throw new Error(`Asset ${asset.assetCode} has no Fixed Asset account. Check its category.`);
    if (!byAcct[k]) byAcct[k] = { amount: 0, codes: [] };
    byAcct[k].amount = round2(byAcct[k].amount + amount);
    byAcct[k].codes.push(asset.assetCode);
  });
  const accts = await Promise.all(Object.keys(byAcct).map(id => resolveAccountById(companyId, id)));
  const lines = [];
  Object.keys(byAcct).forEach((id, i) => {
    const acct = accts[i];
    if (!acct?.id) throw new Error(`Fixed Asset account not found for ${byAcct[id].codes.join(', ')}.`);
    const codes = byAcct[id].codes;
    lines.push({
      accountId: acct.id, accountCode: acct.code, accountName: acct.name,
      debitAmount: byAcct[id].amount, creditAmount: 0,
      description: `Appreciation — ${codes.length <= 5 ? codes.join(', ') : `${codes.length} assets`}`,
    });
  });
  const total = round2(items.reduce((s, x) => s + x.amount, 0));
  lines.push({
    accountId: surplusAcct.id, accountCode: surplusAcct.code, accountName: surplusAcct.name,
    debitAmount: 0, creditAmount: total, description: `Revaluation surplus — ${docDate}`,
  });
  return { lines, total };
};

/** Write new appreciation entries to assets: changes = { assetId: newEntries[] }. */
const applyAppreciationEntries = async (companyId, changes, byId, userId) => {
  const ids = Object.keys(changes).filter(id => byId[id]);
  for (let i = 0; i < ids.length; i += 400) {
    const batch = writeBatch(db);
    ids.slice(i, i + 400).forEach(id => {
      const a = byId[id];
      const entries = changes[id];
      const total = sumEntries(entries);
      const next = { ...a, appreciationAmount: total, appreciationEntries: entries };
      const nbv = bookValueOf(next);
      batch.update(doc(db, 'companies', companyId, 'fixedAssets', id), {
        appreciationAmount: total,
        appreciationEntries: entries,
        netBookValue: nbv,
        // A fully depreciated asset becomes Active again when there is value left to depreciate.
        status: (a.status === 'Active' || a.status === 'FullyDepreciated') ? statusForNbv(next, nbv) : a.status,
        updatedAt: new Date().toISOString(),
        updatedBy: userId || '',
      });
    });
    // eslint-disable-next-line no-await-in-loop
    await batch.commit();
  }
};

const appreciationLabel = (remarks) => `Asset Appreciation${remarks ? ` — ${remarks}` : ''}`;

/**
 * Post an appreciation voucher. rows = [{ assetId, amount }] (amount > 0).
 * @returns {{ transactionId, docNumber, total }}
 */
export const postAppreciation = async (companyId, { rows, docDate, remarks = '', financialYearId, branchId = null, branchName = null, userId }) => {
  const wanted = (rows || []).filter(r => r.assetId && round2(r.amount) > 0);
  if (!wanted.length) throw new Error('Enter the appreciation for at least one asset.');
  if (!docDate) throw new Error('Select the appreciation date.');
  const assets = await loadFixedAssets(companyId);
  const byId = {}; assets.forEach(a => { byId[a.id] = a; });
  const items = wanted.map(r => {
    const a = byId[r.assetId];
    if (!a) throw new Error('An asset in this list no longer exists. Reload and try again.');
    if (a.status !== 'Active' && a.status !== 'FullyDepreciated') throw new Error(`${a.assetCode} is ${String(a.status || 'not active').toLowerCase()} and can't be appreciated.`);
    if (a.acquisitionDate && docDate < a.acquisitionDate) throw new Error(`The date can't be before ${a.assetCode} was acquired (${a.acquisitionDate}).`);
    return { asset: a, amount: round2(r.amount) };
  });

  const surplusAcct = await ensureRevaluationSurplusAccount(companyId, userId);
  const { lines, total } = await buildAppreciationLines(companyId, items, surplusAcct, docDate);
  const snapshot = items.map(({ asset, amount }) => {
    const before = bookValueOf(asset);
    return { assetId: asset.id, assetCode: asset.assetCode, assetName: asset.assetName, categoryName: asset.categoryName || '', bookValueBefore: before, amount, newValue: round2(before + amount) };
  });

  const posted = await postFixedAssetJournal(companyId, {
    tranTypeCode: 'APR',
    docDate,
    narration: appreciationLabel(remarks),
    financialYearId,
    branchId,
    branchName,
    userId,
    lines,
    extraFields: { appreciationSnapshot: snapshot, remarks: remarks || '' },
  });

  const changes = {};
  items.forEach(({ asset, amount }) => {
    const prev = Array.isArray(asset.appreciationEntries) ? asset.appreciationEntries : [];
    changes[asset.id] = [...prev, { transactionId: posted.transactionId, docNumber: posted.docNumber, date: docDate, amount }];
  });
  await applyAppreciationEntries(companyId, changes, byId, userId);
  return { ...posted, total };
};

/** Remove this voucher's entry from assets that hold it but are not in keepIds. */
const dropStrayEntries = (assets, voucherId, keepIds, changes) => {
  assets.forEach(a => {
    if (keepIds.has(a.id) || changes[a.id]) return;
    const entries = Array.isArray(a.appreciationEntries) ? a.appreciationEntries : [];
    if (entries.some(e => e.transactionId === voucherId)) changes[a.id] = entries.filter(e => e.transactionId !== voucherId);
  });
};

/** Checks before an appreciation is reduced / removed / re-dated on these assets. */
const assertAppreciationChangeOk = (changes, byId) => {
  const disposed = Object.keys(changes).map(id => byId[id]).filter(a => a && a.status === 'Disposed');
  if (disposed.length) {
    throw new Error(`These assets were disposed after this appreciation: ${disposed.map(a => a.assetCode).join(', ')}. Reverse their disposal first.`);
  }
  // Depreciation already charged on appreciation must still fit in what is
  // left (straight line: only appreciation dated with life left counts).
  const charged = Object.keys(changes).map(id => byId[id])
    .filter(a => {
      if (!a) return false;
      const next = { ...a, appreciationAmount: sumEntries(changes[a.id]), appreciationEntries: changes[a.id] };
      return (Number(a.appreciationDepreciation) || 0) > depreciableAppreciationOf(next) + 0.005;
    });
  if (charged.length) {
    throw new Error(`Depreciation has already been charged on the appreciation of: ${charged.map(a => a.assetCode).join(', ')}. Delete or edit the later depreciation runs first.`);
  }
};

/**
 * Edit an appreciation voucher's amounts / date / remarks. rows = [{ assetId, amount }]
 * for the assets already in it (amount 0 = take that asset out).
 */
export const updateAppreciation = async (companyId, voucher, { rows, docDate, remarks, userId }) => {
  const assets = await loadFixedAssets(companyId);
  const byId = {}; assets.forEach(a => { byId[a.id] = a; });
  const date = docDate || voucher.docDate;
  const oldSnap = voucher.appreciationSnapshot || [];
  const newAmt = {};
  (rows || []).forEach(r => { if (r.assetId) newAmt[r.assetId] = Math.max(0, round2(r.amount)); });

  const items = [];
  const changes = {};
  oldSnap.forEach(s => {
    const a = byId[s.assetId];
    const amount = newAmt[s.assetId] != null ? newAmt[s.assetId] : round2(s.amount);
    if (!a) {
      if (amount > 0) throw new Error('An asset in this voucher no longer exists.');
      return;
    }
    if (amount > 0) items.push({ asset: a, amount, snap: s });
    // Compare with what the asset actually holds for this voucher, so saving
    // again also repairs an asset a failed earlier save left behind.
    const entries = Array.isArray(a.appreciationEntries) ? a.appreciationEntries : [];
    const mine = entries.filter(e => e.transactionId === voucher.id);
    const inStep = amount > 0
      ? (mine.length === 1 && round2(mine[0].amount) === amount && mine[0].date === date)
      : mine.length === 0;
    if (!inStep) {
      if (amount > 0 && a.acquisitionDate && date < a.acquisitionDate) throw new Error(`The date can't be before ${a.assetCode} was acquired (${a.acquisitionDate}).`);
      const others = entries.filter(e => e.transactionId !== voucher.id);
      changes[a.id] = amount > 0 ? [...others, { transactionId: voucher.id, docNumber: voucher.docNumber, date, amount }] : others;
    }
  });
  if (!items.length) throw new Error('All amounts are zero — delete the voucher instead.');
  // Any other asset still holding an entry for this voucher (e.g. left by an
  // earlier failed save) loses it.
  dropStrayEntries(assets, voucher.id, new Set(items.map(x => x.asset.id)), changes);
  assertAppreciationChangeOk(changes, byId);

  const surplusAcct = await ensureRevaluationSurplusAccount(companyId, userId);
  const { lines } = await buildAppreciationLines(companyId, items, surplusAcct, date);
  const snapshot = items.map(({ asset, amount, snap }) => {
    const before = snap.bookValueBefore != null ? round2(snap.bookValueBefore) : round2(bookValueOf(asset) - round2(snap.amount));
    return { assetId: asset.id, assetCode: asset.assetCode, assetName: asset.assetName, categoryName: asset.categoryName || snap.categoryName || '', bookValueBefore: before, amount, newValue: round2(before + amount) };
  });
  const note = remarks != null ? remarks : (voucher.remarks || '');
  // Assets first: if the voucher save then fails, saving again finishes it.
  await applyAppreciationEntries(companyId, changes, byId, userId);
  await updateFixedAssetJournal(companyId, voucher.id, {
    docDate: date,
    narration: appreciationLabel(note),
    lines,
    userId,
    extraFields: { appreciationSnapshot: snapshot, remarks: note },
  });
};

/** Delete an appreciation voucher: removes its entry and takes the appreciation off each asset. */
export const deleteAppreciation = async (companyId, voucher, userId) => {
  const assets = await loadFixedAssets(companyId);
  const byId = {}; assets.forEach(a => { byId[a.id] = a; });
  const changes = {};
  (voucher.appreciationSnapshot || []).forEach(s => {
    const a = byId[s.assetId];
    if (!a) return;
    changes[a.id] = (a.appreciationEntries || []).filter(e => e.transactionId !== voucher.id);
  });
  dropStrayEntries(assets, voucher.id, new Set(), changes);
  assertAppreciationChangeOk(changes, byId);
  // Assets first: if the voucher delete then fails, deleting again finishes it.
  await applyAppreciationEntries(companyId, changes, byId, userId);
  await deleteTransaction(companyId, voucher.id);
};
